import React, { useReducer, createContext, useEffect, useState } from "react";
import shortid from "shortid";
import { useNavigate, useParams } from "react-router-dom";

import useHttp from "../../../hooks/useHttp";
import {
  BANK_PAYMENT_POST_URL,
  ADMIN_BANK_PAYMENT_INFO_GET,
} from "../../../utils/api-ends";
import LoaderImg from "../../../assets/media/loader.gif";

export const ManageBankPaymentContext = createContext();

const initialState = {
  paymentInfo: {
    bank_account_holder_name: "",
    bank_account_number: "",
    bank_name: "",
    bank_branch_name: "",
    payment_amount: "",
    payment_date: "",
    payment_request_obj: "",
    transaction_id: "",
    transaction_receipt_image: "",
  },
  bank_transaction_receipt_image_url: "",
  errorMessages: {},
  successMsg: "",
};

const reducer = (state, action) => {
  switch (action.type) {
    case "ON_CHANGE_BANK_INFO":
      return {
        ...state,
        paymentInfo: {
          ...state.paymentInfo,
          [action.payload.name]: action.payload.value,
        },
      };
    case "ON_CHANGE_BANK_INFO_FILE":
      let transaction_receipt_img = "";
      if (action.payload.name === "transaction_receipt_image") {
        transaction_receipt_img = URL.createObjectURL(action.payload.value);
      }
      return {
        ...state,
        paymentInfo: {
          ...state.paymentInfo,
          [action.payload.name]: action.payload.value,
        },
        bank_transaction_receipt_image_url: transaction_receipt_img,
      };
    case "APPEND_ERROR_MESSAGES":
      return {
        ...state,
        errorMessages: {
          ...state.errorMessages,
          [action.payload.name]: action.payload.value,
        },
      };
    case "CLEAR_ERROR_MESSAGES":
      return {
        ...state,
        errorMessages: {},
      };
    case "CLEAR_SUCCESS_MESSAGE":
      return {
        ...state,
        successMsg: "",
      };
    case "SET_SUCCESS_MESSAGE":
      return {
        ...state,
        successMsg: action.payload,
      };

    default:
      return state;
  }
};

const BankPayment = ({ paymentRequestDetails }) => {
  const [bankPaymentState, bankPaymentContextDispatch] = useReducer(
    reducer,
    initialState
  );
  const [adminBankInfo, setAdminBankInfo] = useState(null);
  const { paymentInfo, errorMessages, successMsg } = bankPaymentState;
  const { isLoading, error, sendRequests } = useHttp();
  const { sendRequests: fetchAdminBankInfo } = useHttp();
  const navigate = useNavigate();
  const { paymentRequestId } = useParams();

  const onChangeBankInfo = (e) => {
    bankPaymentContextDispatch({
      type: "ON_CHANGE_BANK_INFO",
      payload: {
        name: e.target.name,
        value: e.target.value,
      },
    });
  };

  const onChangeBankInfoFile = (e) => {
    bankPaymentContextDispatch({
      type: "ON_CHANGE_BANK_INFO_FILE",
      payload: {
        name: e.target.name,
        value: e.target.files[0],
      },
    });
  };

  const handleSubmitBankData = () => {
    // Clear fields related errors and success message
    bankPaymentContextDispatch({
      type: "CLEAR_ERROR_MESSAGES",
    });
    bankPaymentContextDispatch({
      type: "CLEAR_SUCCESS_MESSAGE",
    });

    // Validate fields
    let hasErr = false;
    Object.keys(paymentInfo).forEach((key) => {
      if (paymentInfo[key] === "" && key !== "payment_request_obj") {
        hasErr = true;
        bankPaymentContextDispatch({
          type: "APPEND_ERROR_MESSAGES",
          payload: {
            name: key,
            value: ["This field is required"],
          },
        });
      }
    });

    // If there is error then stuck there
    if (hasErr) return;

    // Send request to server
    let formData = new FormData();
    for (var key in paymentInfo) {
      formData.append(key, paymentInfo[key]);
    }
    formData.append("payment_request_obj", paymentRequestId);

    const response = (data) => {
      bankPaymentContextDispatch({
        type: "SET_SUCCESS_MESSAGE",
        payload: "Your bank payment request sent successfully",
      });

      setTimeout(() => {
        navigate(
          `/payment/${paymentRequestId}/response?response_message=Your bank payment request for $${paymentInfo?.payment_amount} has been sent successfully.`
        );
      }, 1000);
    };
    sendRequests(
      {
        url: BANK_PAYMENT_POST_URL,
        method: "post",
        data: formData,
      },
      response
    );
  };

  useEffect(() => {
    if (!["", null, undefined].includes(error)) {
      Object.keys(error).forEach((key) => {
        bankPaymentContextDispatch({
          type: "APPEND_ERROR_MESSAGES",
          payload: {
            name: key,
            value: error[key],
          },
        });
      });
    }
  }, [error]);

  // Show or hide error messages on mouse enter or leave
  const showErrorMessage = (e) =>
    e.currentTarget.setAttribute("show-tittle", "true");
  const hideErrorMessage = (e) =>
    e.currentTarget.setAttribute("show-tittle", "false");

  // Fetch admin bank information
  useEffect(() => {
    const response = (data) => {
      setAdminBankInfo(data);
    };
    fetchAdminBankInfo(
      {
        url: ADMIN_BANK_PAYMENT_INFO_GET,
      },
      response
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Amount assign
  useEffect(() => {
    bankPaymentContextDispatch({
      type: "ON_CHANGE_BANK_INFO",
      payload: {
        name: "payment_amount",
        value: paymentRequestDetails?.amount,
      },
    });
  }, [paymentRequestDetails]);

  return (
    <>
      {successMsg?.length > 0 && (
        <div className="d-flex justify-content-center mb-4">
          <div className="alert alert-success" role="alert">
            {successMsg}
          </div>
        </div>
      )}
      <div className="row">
        <div className="col-lg-4">
          <div className="input-box-area" single-labe="true">
            <label>Bank Name</label>
            <input
              type="text"
              placeholder="Bank Name"
              name="bank_name"
              value={paymentInfo?.bank_name}
              onChange={onChangeBankInfo}
            />

            {errorMessages?.bank_name?.map((err) => (
              <div
                key={shortid.generate()}
                className="error"
                show-tittle="false"
                onMouseEnter={showErrorMessage}
                onMouseLeave={hideErrorMessage}>
                <i className="fa-solid fa-circle-exclamation"></i>
                <p>{err}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="input-box-area" single-labe="true">
            <label>Transaction Id</label>
            <input
              type="text"
              placeholder="Transaction Id"
              name="transaction_id"
              value={paymentInfo?.transaction_id}
              onChange={onChangeBankInfo}
            />
            {errorMessages?.transaction_id?.map((err) => (
              <div
                key={shortid.generate()}
                className="error"
                show-tittle="false"
                onMouseEnter={showErrorMessage}
                onMouseLeave={hideErrorMessage}>
                <i className="fa-solid fa-circle-exclamation"></i>
                <p>{err}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="input-box-area" single-labe="true">
            <label>Bank Ac Holder Name</label>
            <input
              type="text"
              placeholder="Bank Ac Holder Name"
              name="bank_account_holder_name"
              value={paymentInfo?.bank_account_holder_name}
              onChange={onChangeBankInfo}
            />
            {errorMessages?.bank_account_holder_name?.map((err) => (
              <div
                key={shortid.generate()}
                className="error"
                show-tittle="false"
                onMouseEnter={showErrorMessage}
                onMouseLeave={hideErrorMessage}>
                <i className="fa-solid fa-circle-exclamation"></i>
                <p>{err}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="input-box-area" single-labe="true">
            <label>Bank Acount Number</label>
            <input
              type="text"
              placeholder="Bank Acount Number"
              name="bank_account_number"
              value={paymentInfo?.bank_account_number}
              onChange={onChangeBankInfo}
            />
            {errorMessages?.bank_account_number?.map((err) => (
              <div
                key={shortid.generate()}
                className="error"
                show-tittle="false"
                onMouseEnter={showErrorMessage}
                onMouseLeave={hideErrorMessage}>
                <i className="fa-solid fa-circle-exclamation"></i>
                <p>{err}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="input-box-area" single-labe="true">
            <label>Bank Branch Name</label>
            <input
              type="text"
              placeholder="Bank Branch Name"
              name="bank_branch_name"
              value={paymentInfo?.bank_branch_name}
              onChange={onChangeBankInfo}
            />
            {errorMessages?.bank_branch_name?.map((err) => (
              <div
                key={shortid.generate()}
                className="error"
                show-tittle="false"
                onMouseEnter={showErrorMessage}
                onMouseLeave={hideErrorMessage}>
                <i className="fa-solid fa-circle-exclamation"></i>
                <p>{err}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="input-box-area" single-labe="true">
            <label>Payment Amount</label>
            <input
              type="number"
              placeholder="Payment Amount"
              name="payment_amount"
              value={paymentInfo?.payment_amount}
              onChange={onChangeBankInfo}
              disabled
            />
            {errorMessages?.payment_amount?.map((err) => (
              <div
                key={shortid.generate()}
                className="error"
                show-tittle="false"
                onMouseEnter={showErrorMessage}
                onMouseLeave={hideErrorMessage}>
                <i className="fa-solid fa-circle-exclamation"></i>
                <p>{err}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="input-box-area" single-labe="true">
            <label>Payment Date</label>
            <input
              type="date"
              placeholder="Payment Date"
              name="payment_date"
              value={paymentInfo?.payment_date}
              onChange={onChangeBankInfo}
            />
            {errorMessages?.payment_date?.map((err) => (
              <div
                key={shortid.generate()}
                className="error"
                show-tittle="false"
                onMouseEnter={showErrorMessage}
                onMouseLeave={hideErrorMessage}>
                <i className="fa-solid fa-circle-exclamation"></i>
                <p>{err}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="input-box-area" single-labe="true">
            <label> Transaction Receipt Image</label>
            <input
              type="file"
              name="transaction_receipt_image"
              onChange={onChangeBankInfoFile}
            />
            <div className="file-info">
              <i className="fas fa-image"></i>
              <span>
                {paymentInfo?.transaction_receipt_image?.name ||
                  "Choose your image"}
              </span>
            </div>
            {errorMessages?.transaction_receipt_image?.map((err) => (
              <div
                key={shortid.generate()}
                className="error"
                show-tittle="false"
                onMouseEnter={showErrorMessage}
                onMouseLeave={hideErrorMessage}>
                <i className="fa-solid fa-circle-exclamation"></i>
                <p>{err}</p>
              </div>
            ))}
          </div>

          {bankPaymentState?.bank_transaction_receipt_image_url && (
            <a
              href={bankPaymentState?.bank_transaction_receipt_image_url}
              target="_blank"
              rel="noreferrer">
              View Image
            </a>
          )}
        </div>
      </div>

      <div className="multiple-btn mt-4">
        <button className="m-0" onClick={handleSubmitBankData}>
          Payment
          {isLoading && (
            <img src={LoaderImg} style={{ width: "22px" }} alt="" />
          )}
        </button>
      </div>

      <div className="row">
        <div className="col-lg-6">
          <div className="card mt-5">
            <div className="card-header">
              <h3 className="card-title text-center">Admin Bank Information</h3>
            </div>
            <div className="card-body">
              <h6>
                Account Holder Name:{" "}
                <span style={{ color: "grey" }}>
                  {adminBankInfo?.account_holder_name}
                </span>
              </h6>
              <h6>
                Account Number:{" "}
                <span style={{ color: "grey" }}>
                  {adminBankInfo?.account_number}
                </span>
              </h6>
              <h6>
                Branch Name:{" "}
                <span style={{ color: "grey" }}>
                  {adminBankInfo?.bank_branch_name}
                </span>
              </h6>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BankPayment;
